import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import type { NextBestAction } from '@post-office/confluence-next-best-action';
import type { InAppNarrowExposureExperiment } from '@post-office/message-template-contracts/message-template-placement-config';
import type { SerializableRecord } from '@post-office/serializable-record';
import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import {
	TEMPLATE_XFLOW_EXPERIMENT_KEY,
	type PostOfficeSideNavMessageKey,
} from './contexts/PostOfficeSideNavMessageProvider';
import {
	EXPERIMENTAL_MESSAGES,
	NOT_ENROLLED_COHORT,
	RECENT_DOWNGRADE_DISCOUNT_EXPERIMENT,
	RECENT_DOWNGRADE_DISCOUNT_TEMPLATE_ID,
	ROVO_EXTENSION_CTA_TEMPLATE_ID,
} from './experimental-message-list';

// Centralises side-nav eligibility and selection rules shared by the legacy selector and the
// narrow-exposure migration path, keeping the placement component focused on render orchestration.
export const TEMPLATE_XFLOW_GATE = 'twcg-2112-twc-xflow-conf-pages-templates-gate';

export type SideNavMessage = EligibleMessage<SerializableRecord> & {
	inAppNarrowExposureExperiment?: InAppNarrowExposureExperiment;
	explicitEnableRequired?: boolean;
	experimentKey?: string;
	nextBestActionType?: NextBestAction;
	tapReminderTrait?: string;
};

export type SideNavMessageContext = {
	enabledMessages: PostOfficeSideNavMessageKey[];
};

export type DisplayedMessage = {
	message: SideNavMessage;
	// TODO PO-10820: Remove the cohort prop and renderer plumbing with the Rovo CTA experiment.
	experimentCohort?: string;
};

// Apply placement-specific and non-standard experiment eligibility before the generic hook runs,
// preserving the original custom behavior without logging an unintended experiment exposure.
export const filterEligibleMessages = (
	messages: SideNavMessage[],
	context: SideNavMessageContext | null,
	currentRoute: string | undefined,
): SideNavMessage[] =>
	messages.filter((message) => {
		if (message.explicitEnableRequired) {
			const isTemplateXflowMessage = message.experimentKey === TEMPLATE_XFLOW_EXPERIMENT_KEY;
			const explicitMessageKey = isTemplateXflowMessage
				? TEMPLATE_XFLOW_EXPERIMENT_KEY
				: message.nextBestActionType;
			const isExplicitlyEnabled =
				explicitMessageKey !== undefined && context?.enabledMessages.includes(explicitMessageKey);

			if (isTemplateXflowMessage) {
				const hasReminderTrait = message.tapReminderTrait === 'remind';

				return (
					FeatureGates.checkGate(TEMPLATE_XFLOW_GATE) && (hasReminderTrait || isExplicitlyEnabled)
				);
			}

			return isExplicitlyEnabled;
		}

		if (message.messageTemplateId === ROVO_EXTENSION_CTA_TEMPLATE_ID) {
			// Don't show on FPS to avoid collision with existing install CTA (built outside of PO)
			if (currentRoute?.includes('/search')) {
				return false;
			}
		}

		return true;
	});

// Template xflow is not a standard narrow-exposure experiment, so its original explicit
// enablement and ranking override are resolved before either selection path.
export const findExplicitTemplateXflowMessage = (
	messages: SideNavMessage[],
	context: SideNavMessageContext | null,
): SideNavMessage | undefined =>
	context?.enabledMessages.includes(TEMPLATE_XFLOW_EXPERIMENT_KEY)
		? messages.find((message) => message.experimentKey === TEMPLATE_XFLOW_EXPERIMENT_KEY)
		: undefined;

// Apply custom eligibility before narrow-exposure evaluation. The hook itself evaluates messages
// in rank order, so later experiments are reached only after earlier candidates are dropped.
export const candidatesForNarrowExposure = (messages: SideNavMessage[]): SideNavMessage[] => {
	// TODO PO-10820: Remove this Rovo-specific gate handling when its experiment is cleaned up.
	const isRovoExperimentEnabled = FeatureGates.checkGate(
		'po_enable_confluence-side-nav-exp-messages',
	);

	return messages.flatMap((message) => {
		if (message.messageTemplateId === ROVO_EXTENSION_CTA_TEMPLATE_ID && !isRovoExperimentEnabled) {
			// The legacy path treats Rovo as a normal message while its experiment gate is off.
			return [{ ...message, inAppNarrowExposureExperiment: undefined }];
		}

		const experimentalMessage = EXPERIMENTAL_MESSAGES.find(
			(candidate) => candidate.messageTemplateId === message.messageTemplateId,
		);

		if (
			experimentalMessage?.shouldProceedToStatsigEvaluation &&
			!experimentalMessage.shouldProceedToStatsigEvaluation()
		) {
			return [];
		}

		return [message];
	});
};

// Rovo needs its cohort in the renderer to select copy and attach analytics metadata. Other
// experiments only use their cohort to decide whether the hook should display them.
export const experimentCohortForMessage = (message: SideNavMessage): string | undefined => {
	// TODO PO-10820: Can remove this function when the Rovo CTA experiment is cleaned up.
	if (message.messageTemplateId !== ROVO_EXTENSION_CTA_TEMPLATE_ID) {
		return undefined;
	}

	const config = message.inAppNarrowExposureExperiment;
	if (!config) {
		return undefined;
	}

	// oxlint-disable-next-line post-office/no-manual-in-app-experiments -- Preserve the existing Rovo CTA cohort prop until this experiment is cleaned up.
	const cohort = expVal<unknown>(config.statsigExperiment, config.statsigParameterName, null);
	return typeof cohort === 'string' ? cohort : undefined;
};

// Preserve the original selection behavior while migration gates are off.
export const selectLegacyDisplayedMessage = (
	messages: SideNavMessage[],
	context: SideNavMessageContext | null,
): DisplayedMessage | undefined => {
	if (!messages.length) {
		return;
	}

	const explicitlyEnabledTemplateXflowMessage = findExplicitTemplateXflowMessage(messages, context);

	if (explicitlyEnabledTemplateXflowMessage) {
		return { message: explicitlyEnabledTemplateXflowMessage };
	}

	const [topMessage] = messages;

	// The original discount experiment only records exposure when its message is first in ranking.
	if (topMessage?.messageTemplateId === RECENT_DOWNGRADE_DISCOUNT_TEMPLATE_ID) {
		// oxlint-disable-next-line post-office/no-manual-in-app-experiments -- Legacy selection is retained while migration gates are disabled.
		const recentDowngradeDiscountCohort = FeatureGates.getExperimentValue<string>(
			RECENT_DOWNGRADE_DISCOUNT_EXPERIMENT,
			'cohort',
			NOT_ENROLLED_COHORT,
		);

		if (recentDowngradeDiscountCohort === 'variation') {
			return { message: topMessage };
		}
	}

	const candidateMessages = messages.filter(
		(message) => message.messageTemplateId !== RECENT_DOWNGRADE_DISCOUNT_TEMPLATE_ID,
	);

	// TODO PO-10820: Remove this legacy Rovo experiment branch when the experiment is cleaned up.
	// https://switcheroo.atlassian.com/ui/gates/f775d661-abc2-488d-b58c-c28fab9bd58f/key/po_enable_confluence-side-nav-exp-messages
	if (FeatureGates.checkGate('po_enable_confluence-side-nav-exp-messages')) {
		for (const message of candidateMessages) {
			const experimentalMessage = EXPERIMENTAL_MESSAGES.find(
				(candidate) => candidate.messageTemplateId === message.messageTemplateId,
			);

			if (!experimentalMessage) {
				return { message };
			}

			if (
				experimentalMessage.shouldProceedToStatsigEvaluation &&
				!experimentalMessage.shouldProceedToStatsigEvaluation()
			) {
				continue;
			}

			// oxlint-disable-next-line post-office/no-manual-in-app-experiments -- Legacy selection is retained while migration gates are disabled.
			const experimentCohort = FeatureGates.getExperimentValue<string>(
				experimentalMessage.statsigExperiment,
				experimentalMessage.statsigParameterName,
				NOT_ENROLLED_COHORT,
			);

			if (experimentalMessage.statsigTestCohortPredicate(experimentCohort)) {
				return { message, experimentCohort };
			}
		}

		return undefined;
	}

	return candidateMessages[0] ? { message: candidateMessages[0] } : undefined;
};
